import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { Close, Calculate, Save } from '@mui/icons-material';
import { Treatment } from '../../types/treatment.types';
import { CalculatedDosageData } from '../../types/treatment.types';
import { Patient } from '../../contexts/api-contexts/CasesContext';
import { DrugSubstanceDosesProvider } from '../../contexts/api-contexts/DrugSubstanceDosesContext';
import { DrugDosageCalculatorWithInfo } from '../drug-dosage-calculator/DrugDosageCalculatorWithInfo';
import {
  CalculationType,
  CalculationUnit,
  CalculatedValues,
  DrugMgPerUnit,
  DrugUnit,
} from '../../types/DrugDosageCalculator.type';
import {
  DoseAlternative,
  DrugSubstanceDose,
} from '../../types/DrugSubstanceDose.type';

// Helper function to get strength unit label from unit value
const getStrengthUnitLabel = (
  calculationType: CalculationType,
  unit: number
): string => {
  if (calculationType === 'drugStrength') {
    // For drugStrength, the result is the strength, so we need to derive the strength unit
    // from the DrugUnit (ml -> mg/ml, tablet(s) -> mg/tablet, etc.)
    switch (unit) {
      case DrugUnit['ml']:
        return 'mg/ml';
      case DrugUnit['tablet(s)']:
        return 'mg/tablet';
      case DrugUnit['capsule(s)']:
        return 'mg/capsule';
      default:
        return 'mg/ml';
    }
  }
  // For numberOfUnits and drugAmount, the unit is DrugMgPerUnit
  switch (unit) {
    case DrugMgPerUnit['mg/ml']:
      return 'mg/ml';
    case DrugMgPerUnit['mg/tablet']:
      return 'mg/tablet';
    case DrugMgPerUnit['mg/capsule']:
      return 'mg/capsule';
    default:
      return 'mg/ml';
  }
};

// Helper function to get amount unit label from unit value
const getAmountUnitLabel = (
  calculationType: CalculationType,
  unit: number
): string => {
  if (calculationType === 'drugStrength') {
    // For drugStrength, the unit is DrugUnit
    switch (unit) {
      case DrugUnit['ml']:
        return 'ml';
      case DrugUnit['tablet(s)']:
        return 'tablet(s)';
      case DrugUnit['capsule(s)']:
        return 'capsule(s)';
      default:
        return 'ml';
    }
  }
  // For numberOfUnits and drugAmount, derive amount unit from DrugMgPerUnit
  switch (unit) {
    case DrugMgPerUnit['mg/ml']:
      return 'ml';
    case DrugMgPerUnit['mg/tablet']:
      return 'tablet(s)';
    case DrugMgPerUnit['mg/capsule']:
      return 'capsule(s)';
    default:
      return 'ml';
  }
};

interface CaseDosageCalculatorDialogProps {
  open: boolean;
  onClose: () => void;
  treatment: Treatment | null;
  patient?: Patient;
  /** Disease name for context display */
  diseaseName?: string;
  /** Animal type name for context display */
  animalTypeName?: string;
  /** Whether to show the Save button (for case context) */
  showSaveButton?: boolean;
  /** Callback when Save is clicked with calculated dosage data */
  onSave?: (dosageData: CalculatedDosageData) => void;
}

const CaseDosageCalculatorDialog: React.FC<CaseDosageCalculatorDialogProps> = ({
  open,
  onClose,
  treatment,
  patient,
  diseaseName,
  animalTypeName,
  showSaveButton = false,
  onSave,
}) => {
  // State to track the latest calculation
  const [lastCalculation, setLastCalculation] =
    useState<CalculatedDosageData | null>(null);

  // Handle calculation from the calculator
  const handleCalculation = useCallback(
    (
      calculationType: CalculationType,
      values: CalculatedValues<CalculationUnit>,
      selectedAlternative?: DoseAlternative,
      selectedDose?: DrugSubstanceDose
    ) => {
      const dosageData: CalculatedDosageData = {
        calculation_type: calculationType,
        weight_kg: patient?.weight_kg || 0,
        strength: values.strength,
        unit: values.unit,
        strength_unit: getStrengthUnitLabel(calculationType, values.unit),
        amount_unit: getAmountUnitLabel(calculationType, values.unit),
        dose: values.dose,
        result: values.result,
        dose_alternative_id: selectedAlternative?.id,
        dose_indication_name: selectedDose?.drug_indication.name,
        dose_type_name: selectedAlternative?.phases[0]?.dose_type.name,
        administration_route_name:
          selectedAlternative?.phases[0]?.drug_administration_route.name,
      };
      setLastCalculation(dosageData);
    },
    [patient?.weight_kg]
  );

  // Handle save button click
  const handleSave = useCallback(() => {
    if (lastCalculation && onSave) {
      onSave(lastCalculation);
      onClose();
    }
  }, [lastCalculation, onSave, onClose]);

  // Reset calculation state when dialog closes
  const handleClose = useCallback(() => {
    setLastCalculation(null);
    onClose();
  }, [onClose]);

  if (!treatment) {
    return null;
  }

  const drugSubstanceId = treatment.drug_substance?.id;
  const drugSubstanceName = treatment.drug_substance?.name;
  const patientWeight = patient?.weight_kg;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="case-dosage-calculator-dialog-title"
    >
      <DialogTitle id="case-dosage-calculator-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Calculate sx={{ color: 'primary.main' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Drug Dosage Calculator
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {drugSubstanceId ? (
          <DrugSubstanceDosesProvider>
            <DrugDosageCalculatorWithInfo
              drugSubstanceId={drugSubstanceId}
              drugSubstanceName={drugSubstanceName}
              diseaseName={diseaseName}
              animalTypeName={animalTypeName}
              patientWeightKg={patientWeight}
              weightDisabled={!!patientWeight}
              treatmentId={treatment.id}
              onCalculation={handleCalculation}
            />
          </DrugSubstanceDosesProvider>
        ) : (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: 'action.hover',
              borderRadius: 2,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No drug substance associated with this treatment.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
        {showSaveButton && onSave && (
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            startIcon={<Save />}
            disabled={!lastCalculation}
          >
            Save Dosage
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CaseDosageCalculatorDialog;
